import SEAT_IMAGE from "../../assets/images/seat.png";
import SEAT_NOT_AVAILABLE_IMAGE from "../../assets/images/seat_not_available.png";
import GALLARY from "../../assets/images/galley.png";
import LAVATORY from "../../assets/images/lavatory.png";
import { Tooltip } from "antd";
import { formatIndianNumber } from "../../_helpers/formatUtils";
import { SEAT_LAYOUT } from "../../_constants/constant";
import { memo, useCallback, useMemo } from "react";
import classNames from "classnames";

interface Seat {
  SSR_Key: string;
  SSR_TypeName: string;
  SSR_TypeDesc: string;
  SSR_Status: number;
  Total_Amount: number;
}

interface SeatRow {
  Seat_Details: Seat[];
}

interface SeatSelectionSlideProps {
  seat_row: { Seat_Row: SeatRow[] };
  index: number;
  travellers: any[];
  selectedSeats: Seat[];
  toggleTotalSeatSelection: (seat: Seat, index: number) => void;
  OriginCity: string;
  DestinationCity: string;
}

interface SeatCellProps {
  seat: Seat | undefined;
  col: string;
  rowNumber: number;
  isSelected: boolean;
  handleSeatClick: (seat: Seat, index: number) => void;
  index: number;
}

const SeatCell = memo(
  ({
    seat,
    col,
    rowNumber,
    isSelected,
    handleSeatClick,
    index,
  }: SeatCellProps) => {
    const seatCode = `${rowNumber}${col}`;

    if (!seat) {
      return (
        <div className="seat-column m-custom-2" key={seatCode}>
          <div className="seatEmptyLabel" />
        </div>
      );
    }

    const isAvailable = seat.SSR_Status === 1;
    const isBlocked = seat.SSR_Status === 2;
    const isBooked = seat.SSR_Status === 3;

    const tooltipContent = isAvailable ? (
      <p className="tooltip-style">
        {seat.SSR_TypeDesc} |{" "}
        <span className="fw-700">₹{seat.Total_Amount}</span>
      </p>
    ) : (
      <p className="tooltip-style">
        {isBlocked
          ? "Sorry! This seat is blocked"
          : "Sorry! This seat is booked"}
      </p>
    );

    const seatContent = isAvailable ? (
      <div
        className={classNames("seat-box", {
          "selected-seat-bg": isSelected,
          "seat-not-available": !isSelected,
        })}
      >
        {isSelected ? (
          <p className="icon-checkmark m-0" />
        ) : (
          <img
            src={SEAT_IMAGE}
            alt="seatAvailable"
            className={classNames("opacity-half", {
              "free-seat-bg": seat.Total_Amount === 0,
            })}
          />
        )}
      </div>
    ) : (
      <div className="seat-not-available">
        <img
          src={isBooked ? SEAT_NOT_AVAILABLE_IMAGE : SEAT_IMAGE}
          alt={isBooked ? "seatBooked" : "seatBlocked"}
          className={classNames({
            "blocked-ticket-bg opacity-half": isBlocked,
          })}
        />
      </div>
    );

    return (
      <Tooltip
        key={seatCode}
        title={tooltipContent}
        placement="topRight"
        color="#fff"
      >
        <div
          className={classNames("seat-column m-custom-2", {
            "blocked-ticket": !isAvailable,
          })}
          onClick={isAvailable ? () => handleSeatClick(seat, index) : undefined}
        >
          <div className="relative" id={seatCode}>
            {seatContent}
          </div>
        </div>
      </Tooltip>
    );
  }
);

const PassangerWiseSeats = memo(({ selectedSeats, travellers, index }: any) => {
  return (
    <div className="seat-passanger-list">
      <ul className="passanger-row-list">
        {selectedSeats
          .filter((e: any) => e?.segmentindex === index)
          .map((seat: any, i: number) => (
            <li key={i} className="passanger-row">
              <span className="fw-600 m-0">{`${travellers?.[i]?.initial} ${travellers?.[i]?.first_name}  ${travellers?.[i]?.last_name}  - ${seat.SSR_TypeName}`}</span>
            </li>
          ))}
      </ul>
    </div>
  );
});

const SeatInfoCard = memo(() => (
  <div className="seat-info-card">
    <ul>
      {[
        {
          img: SEAT_IMAGE,
          alt: "seatAvailable",
          label: "Available Seat",
          className: "opacity-half",
        },
        {
          img: SEAT_IMAGE,
          alt: "seatAvailable",
          label: "Free Seat",
          className: "opacity-half free-seat-bg",
        },
        {
          img: null,
          alt: "",
          label: "Selected Seat",
          className: "selected-seat-bg selected-li-bg",
          content: <p className="icon-checkmark m-0" />,
        },
        {
          img: SEAT_IMAGE,
          alt: "seatAvailable",
          label: "Blocked Seat",
          className: "opacity-half blocked-ticket-bg",
        },
        {
          img: SEAT_NOT_AVAILABLE_IMAGE,
          alt: "seatBooked",
          label: "Booked Seat",
          className: "",
        },
      ].map((item, index) => (
        <li className="seat-info-row" key={index}>
          {item.img ? (
            <img
              src={item.img}
              alt={item.alt}
              className={item.className}
              style={{ height: 29, width: 29 }}
            />
          ) : (
            <div className={item.className}>{item.content}</div>
          )}
          <span className="fw-700">{item.label}</span>
        </li>
      ))}
    </ul>
  </div>
));

const SeatSelectionSlide = ({
  seat_row,
  index,
  travellers,
  selectedSeats,
  toggleTotalSeatSelection,
  OriginCity,
  DestinationCity,
}: SeatSelectionSlideProps) => {
  const handleSeatClick = useCallback(
    (seat: Seat, index: number) => toggleTotalSeatSelection(seat, index),
    [toggleTotalSeatSelection]
  );

  const totalAmount = useMemo(
    () =>
      selectedSeats.reduce((acc, seat) => acc + (seat?.Total_Amount || 0), 0),
    [selectedSeats]
  );

  const seatDetails = useMemo(() => seat_row?.Seat_Row || [], [seat_row]);

  const rowNumbers = useMemo(() => {
    const numbers = seatDetails
      .map((row) => {
        const firstSeat = row?.Seat_Details?.[0];
        const rowNumber = parseInt(
          firstSeat?.SSR_TypeName?.match(/^\d+/)?.[0] || "NaN"
        );
        return isNaN(rowNumber) ? null : rowNumber;
      })
      .filter((num): num is number => num !== null)
      .sort((a, b) => a - b);

    if (numbers.length === 0) return [];
    const minRow = Math.min(...numbers);
    const maxRow = Math.max(...numbers);
    return Array.from({ length: maxRow - minRow + 1 }, (_, i) => minRow + i);
  }, [seatDetails]);

  const headerColumns = [
    { type: "empty" },
    { type: "lavatory", img: LAVATORY },
    { type: "empty" },
    { type: "empty" },
    { type: "empty" },
    { type: "empty" },
    { type: "empty" },
    { type: "lavatory", img: LAVATORY },
    { type: "empty" },
  ];

  const footerColumns = [
    { type: "empty" },
    { type: "galley", img: GALLARY },
    { type: "lavatory", img: LAVATORY },
    { type: "empty" },
    { type: "empty" },
    { type: "empty" },
    { type: "lavatory", img: LAVATORY },
    { type: "galley", img: GALLARY },
    { type: "empty" },
  ];

  return (
    <div
      className="w-100"
      style={{
        backgroundColor: "white",
        boxShadow: "0 0 5px #00000026",
        borderRadius: "5px",
        overflow: "hidden",
      }}
    >
      <div
        className="p-2 w-100"
        style={{ borderBottom: "1px solid lightgray" }}
      >
        <div className="route-seat-selection-title">
          <h4 className="m-0">
            {OriginCity} → {DestinationCity}
          </h4>
          <span
            className={classNames("m-0", {
              "pending-selection": selectedSeats.length === 0,
              "seat-amount": selectedSeats.length > 0,
            })}
          >
            {selectedSeats.length === 0
              ? "Selection pending"
              : `₹ ${formatIndianNumber(totalAmount)}`}
          </span>
        </div>
        <div className="route-seat-selection-title">
          <span className="m-0">
            {selectedSeats.length} of {travellers.length} Seat(s) Selected
          </span>
          {selectedSeats.length > 0 && (
            <span className="m-0">Added to fare</span>
          )}
        </div>
      </div>
      <div className="position-relative">
        <div className="grid grid-cols-4 main-seatselection-layout">
          <SeatInfoCard />
          {selectedSeats.filter((e: any) => e?.segmentindex === index)?.length >
            0 && (
            <PassangerWiseSeats
              selectedSeats={selectedSeats}
              travellers={travellers}
              index={index}
            />
          )}
          <div className="flight-wrap">
            <div className="flight-top-position">
              <span className="bgProperties iconflFront flight-top-bg" />
            </div>
            <div className="flight-seat-matrix">
              <div className="seatRow">
                {headerColumns.map((col, i) => (
                  <div className="seat-column m-custom-2" key={i}>
                    <div
                      className={
                        col.type === "empty"
                          ? "emptyCell"
                          : col.type === "lavatory"
                          ? "lavatoryWrap"
                          : "galleyWrap"
                      }
                    >
                      {col.img && <img src={col.img} alt={col.type} />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="seatRow">
                {[
                  { type: "empty" },
                  { type: "exit", side: "left" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "exit", side: "right" },
                  { type: "empty" },
                ].map((col, i) => (
                  <div className="seat-column m-custom-2" key={i}>
                    <div
                      className={
                        col.type === "empty"
                          ? "emptyCell"
                          : `exitIcon ${col.side}`
                      }
                    >
                      {col.type === "exit" && (
                        <span className={`exit-icon exit-${col.side}`}>
                          Exit
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="seatRow">
                {["", "A", "B", "C", "", "D", "E", "F", ""].map((label, i) => (
                  <div className="seat-column m-custom-2" key={i}>
                    <div
                      data-test={`component-seat_${
                        label ? "label" : "empty_label"
                      }`}
                      className={label ? "seat-label" : "seatEmptyLabel"}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              {rowNumbers.map((rowNumber) => {
                const rowSeats =
                  seatDetails.find((row) => {
                    const first = row?.Seat_Details?.[0];
                    const num = parseInt(
                      first?.SSR_TypeName?.match(/^\d+/)?.[0] || "NaN"
                    );
                    return num === rowNumber;
                  })?.Seat_Details || [];

                return (
                  <div className="seatRow" key={`seat_row_${rowNumber}`}>
                    <div className="seat-column m-custom-2">
                      <div
                        data-test="component-seat_label"
                        className="seat-label"
                      >
                        {rowNumber}
                      </div>
                    </div>
                    {SEAT_LAYOUT.map((col, innerIndex) => {
                      if (col === null) {
                        return (
                          <div
                            className="seat-column m-custom-2"
                            key={`space-${innerIndex}`}
                          >
                            <div className="seatEmptyLabel" />
                          </div>
                        );
                      }

                      const seatCode = `${rowNumber}${col}`;
                      const seat = rowSeats.find(
                        (s) => s?.SSR_TypeName === seatCode
                      );
                      const isSelected = selectedSeats.some(
                        (selected) => selected.SSR_Key === seat?.SSR_Key
                      );

                      return (
                        <SeatCell
                          key={seatCode}
                          seat={seat}
                          col={col}
                          rowNumber={rowNumber}
                          isSelected={isSelected}
                          handleSeatClick={handleSeatClick}
                          index={index}
                        />
                      );
                    })}
                    <div className="seat-column m-custom-2">
                      <div
                        data-test="component-seat_label"
                        className="seat-label"
                      >
                        {rowNumber}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="seatRow">
                {footerColumns.map((col, i) => (
                  <div className="seat-column m-custom-2" key={i}>
                    <div
                      className={
                        col.type === "empty"
                          ? "emptyCell"
                          : col.type === "lavatory"
                          ? "lavatoryWrap"
                          : "galleyWrap"
                      }
                    >
                      {col.img && <img src={col.img} alt={col.type} />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="seatRow">
                {[
                  { type: "empty" },
                  { type: "exit", side: "left" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "empty" },
                  { type: "exit", side: "right" },
                  { type: "empty" },
                ].map((col, i) => (
                  <div className="seat-column m-custom-2" key={i}>
                    <div
                      className={
                        col.type === "empty"
                          ? "emptyCell"
                          : `exitIcon ${col.side}`
                      }
                    >
                      {col.type === "exit" && (
                        <span className={`exit-icon exit-${col.side}`}>
                          Exit
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="bgProperties iconflTail flight-bottom-bg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SeatSelectionSlide);
